import { useCallback, useEffect } from 'react';
import { takeUntil, withLatestFrom } from 'rxjs';
import { filterWidgetEvent } from '@client/foundation/shared/utils/filter-widget-event';
import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import type { WidgetPayload } from '@client/widgets/widget.types';
import { useApplyTemplate } from './apply-template.hook';
import { useDeleteTemplates } from './delete-templates.hook';
import { useSetDefaultTemplate } from './set-default-template';
import { useModifyTemplate } from './modify-template.hook';
import { useDuplicateTemplate } from './duplicate-template.hook';
import { useWidget } from '@client/widgets/widget.hook';
import { useDraftState } from '@client/state/draft-form-state.hook';
import { useWindow } from '@client/window/window.hook';
import { useTradingWorkspaceDestroy$ } from '@client/features/trading-workspace/trading-workspace-scope.context';
import { openManageTradingTemplatesWindow } from '../manage-trading-templates-window.utils';
import { useSharedTemplateActions } from '../shared-template-actions.hook';
import { templateValueToFormIndicators } from '../indicators-template-resolver';
import { templateValueToFormStrategy } from '../strategy-template-resolver';
import type { TradingTemplate } from '@server/trading-templates/schemas/output.schema';
import type { TemplateType } from '@server/trading-templates/schemas/input.schema';

export const useManageTradingTemplates = () => {
  const { openWidget$ } = useWidget();
  const { openWindow, openWindow: openTemplateWindow } = useWindow();
  const { notify } = useNotifications();
  const { state$ } = useDraftState();
  const workspaceDestroy$ = useTradingWorkspaceDestroy$();

  const { onApply } = useApplyTemplate();
  const { onDelete } = useDeleteTemplates();
  const { onSetAsDefault } = useSetDefaultTemplate();
  const { onModify } = useModifyTemplate();
  const { onDuplicate } = useDuplicateTemplate();

  const openTemplateEditor = useCallback(
    ({
      templateId,
      templateName,
      initialConfig,
      onSaved
    }: {
      templateId?: string;
      templateName: string;
      initialConfig: TradingTemplate['config'];
      onSaved?: (savedTemplate: {
        templateId: string;
        name: string;
        config: TradingTemplate['config'];
      }) => void;
    }) => {
      switch (initialConfig.type) {
        case 'indicators':
          openTemplateWindow({
            type: 'IndicatorTemplateEditor',
            componentProps: {
              templateId,
              initialName: templateName,
              initialIndicators: templateValueToFormIndicators(
                initialConfig.value
              ),
              onSaved
            }
          });
          return;
        case 'strategy':
          openTemplateWindow({
            type: 'StrategyTemplateEditor',
            componentProps: {
              templateId,
              initialName: templateName,
              initialStrategy: templateValueToFormStrategy(initialConfig.value),
              onSaved
            }
          });
          return;
        case 'signalBlock':
          openTemplateWindow({
            type: 'SignalBlockTemplateEditor',
            componentProps: {
              templateId,
              initialName: templateName,
              initialConditions: initialConfig.value.conditions,
              onSaved
            }
          });
          return;
        default:
          notify({
            type: 'error',
            message: `Shared ${initialConfig.type} templates cannot be modified here`
          });
      }
    },
    [notify, openTemplateWindow]
  );

  const marketSharedActions = useSharedTemplateActions({
    templateType: 'market',
    openTemplateEditor
  });
  const positionManagementSharedActions = useSharedTemplateActions({
    templateType: 'positionManagement',
    openTemplateEditor
  });
  const indicatorsSharedActions = useSharedTemplateActions({
    templateType: 'indicators',
    openTemplateEditor
  });
  const strategySharedActions = useSharedTemplateActions({
    templateType: 'strategy',
    openTemplateEditor
  });
  const signalBlockSharedActions = useSharedTemplateActions({
    templateType: 'signalBlock',
    openTemplateEditor
  });

  const getSharedTemplateActions = useCallback(
    (templateType: TemplateType) => {
      switch (templateType) {
        case 'market':
          return marketSharedActions;
        case 'positionManagement':
          return positionManagementSharedActions;
        case 'indicators':
          return indicatorsSharedActions;
        case 'strategy':
          return strategySharedActions;
        case 'signalBlock':
          return signalBlockSharedActions;
      }
    },
    [
      indicatorsSharedActions,
      marketSharedActions,
      positionManagementSharedActions,
      signalBlockSharedActions,
      strategySharedActions
    ]
  );

  useEffect(() => {
    const subscription = openWidget$()
      .pipe(
        filterWidgetEvent<WidgetPayload, 'ManageTradingTemplates'>(
          'ManageTradingTemplates'
        ),
        withLatestFrom(state$),
        takeUntil(workspaceDestroy$)
      )
      .subscribe({
        next: ([{ timeframeIndex, templateType, strategyTarget }, draftState]) => {
          if (!draftState) {
            console.error('No draft state found');
            return;
          }

          try {
            const isDuplicateEnabledType =
              templateType === 'indicators' ||
              templateType === 'strategy' ||
              templateType === 'signalBlock';
            const isAddEnabledType = isDuplicateEnabledType;
            const isSharedModifyEnabledType = isDuplicateEnabledType;
            const sharedTemplateActions = getSharedTemplateActions(templateType);

            openManageTradingTemplatesWindow({
              openWindow,
              templateType,
              strategyTarget,
              onApply: onApply(
                timeframeIndex,
                templateType,
                strategyTarget,
                'replace'
              ),
              onAdd: isAddEnabledType
                ? onApply(timeframeIndex, templateType, strategyTarget, 'add')
                : undefined,
              onDelete,
              onSetAsDefault: onSetAsDefault(templateType),
              onDuplicate: isDuplicateEnabledType
                ? onDuplicate(templateType)
                : undefined,
              onShare: (templateId) => sharedTemplateActions.handleShare(templateId),
              onImportShared: sharedTemplateActions.handleImportShared,
              onModifyShared: isSharedModifyEnabledType
                ? (sharedResourceId, _sharedResourceName) =>
                    sharedTemplateActions.handleModifyShared(sharedResourceId)
                : undefined,
              onRevokeShared: sharedTemplateActions.handleRevokeShared,
              onModify:
                templateType === 'signalBlock'
                  ? undefined
                  : onModify(draftState, timeframeIndex, templateType)
            });
          } catch (error) {
            console.error(
              'Failed to open ManageTradingTemplates window:',
              error
            );
          }
        },
        error: (err) =>
          console.error('Error in ManageTradingTemplates subscription:', err)
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [
    onApply,
    onDelete,
    onDuplicate,
    onModify,
    onSetAsDefault,
    getSharedTemplateActions,
    notify,
    openTemplateEditor,
    openWidget$,
    openWindow,
    state$,
    workspaceDestroy$
  ]);
};
